import {
  CLEAR_SEARCH_RESULTS,
  CLEAR_SEARCH_TEXT,
  SEARCH_FAILED,
  SEARCH_QUERY_UPDATED,
  SEARCH_STARTED,
} from './actionTypes';
import { reverseGeocodeBox } from '../utils/mapHelpers';
import { LocationType } from '../constants';
import { fetchLocations } from './locations';

export const clearSearchResults = () => ({
  type: CLEAR_SEARCH_RESULTS,
});

export const clearSearchText = () => async dispatch => {
  dispatch({ type: CLEAR_SEARCH_TEXT });
};

/**
 * Find which locations exist within the given bounding box's area.
 *
 * Allows for filtering on location types and services provided.
 *
 * @param {Object} query
 * @param {number[]} query.bounds Bounding box to search within.
 * @param {string} query.facilityType
 * @param {string} [query.serviceType]
 * @param {number} [query.page=1]
 * @param {number[]} [query.center] `[latitude, longitude]` of the box center.
 * @param {number} [query.radius]
 * @param {?string} [query.address=null] The human-readable place name already
 * resolved for this search center — pass it to skip a redundant reverse
 * geocode (CMS #20560).
 */
export const searchWithBounds = ({
  bounds,
  facilityType,
  serviceType,
  page = 1,
  center,
  radius,
  address = null,
}) => {
  // Only the PPMS-backed types send a human-readable address alongside the
  // coordinates.
  const needsAddress = [
    LocationType.CC_PROVIDER,
    LocationType.ALL,
    LocationType.URGENT_CARE_PHARMACIES,
    LocationType.URGENT_CARE,
  ];

  return dispatch => {
    dispatch({
      type: SEARCH_STARTED,
      payload: {
        currentPage: page,
        searchBoundsInProgress: true,
      },
    });

    const runSearch = resolvedAddress =>
      fetchLocations(
        resolvedAddress,
        bounds,
        facilityType,
        serviceType,
        page,
        dispatch,
        center,
        radius,
      );

    if (!needsAddress.includes(facilityType)) {
      runSearch(null);
      return;
    }

    // Whatever produced these bounds already resolved a place name for this
    // exact center and stored it as `mapBoxQuery.placeName`. Reverse-geocoding
    // the box center therefore just asks Mapbox to identify the point it handed
    // us a moment ago — and gates the search on that call succeeding, so a
    // Mapbox blip fails a search that needed no geocoding at all (CMS #20560).
    if (address) {
      runSearch(address);
      return;
    }

    // No place name reached us, so fall back to resolving one.
    reverseGeocodeBox(bounds).then(reverseGeocodedAddress => {
      if (!reverseGeocodedAddress) {
        dispatch({
          type: SEARCH_FAILED,
          error:
            'Reverse geocoding failed. See previous errors or network log.',
        });

        return;
      }

      runSearch(reverseGeocodedAddress);
    });
  };
};

/**
 * Sync form state with Redux state.
 * (And implicitly cause updates back in VAMap)
 *
 * @param {Object} query The current state of the Search form
 */

export const updateSearchQuery = query => {
  return {
    type: SEARCH_QUERY_UPDATED,
    payload: { ...query },
  };
};
